import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import * as Analytics from 'analytics/Analytics';
import {makeAnalyticsEventId} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {CREATE_PROCESS_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {CreateDropdownOption} from 'file-browser/CreateDropdownConstants';
import {ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {CreateMenuItem} from 'nav/navbar/CreateMenuConstants';
import {DefaultValuesByColumnType} from 'notebook/structured-tables/StructuredTableConstants';
import CreateProcessModal, {
  TableOrFormRenderOptionsToAnalyticsType,
} from 'processes/modals/CreateProcessModal';
import {userCreatableProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import legacyRunModal from 'ui-library/legacyRunModal';

import {CreateMenuProcessQuery, CreateMenuProcessQueryVariables} from './__generated/CreateMenuProcessQuery';
import {getInitialCreateTaskModalViewPreference} from './modals/CreateProcessModalViewPreference';

type CreateMenuProcessQueryProcessTaskSchema =
  CreateMenuProcessQuery['processTaskSchemasSupportingPlatformFeature'][number];

export type ProcessTaskSchemaForCreate = CreateMenuProcessQueryProcessTaskSchema;

export const CREATE_MENU_PROCESSES_QUERY: DocumentNode = gql`
  query CreateMenuProcessQuery($platformFeature: ProcessPlatformFeatureType!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: $platformFeature) {
      id
      name
      canOnlyBePartOfFlowchart
      taskGroupInstructions
      executionType
      organization
      archiveRecord {
        purpose
      }
      permissions {
        objectsCreatable
      }
    }
  }
`;

export function useProcessTaskSchemasForCreateWithLoading(): Array<ProcessTaskSchemaForCreate> | null {
  const result = useBenchlingQuery<CreateMenuProcessQuery, CreateMenuProcessQueryVariables>(
    CREATE_MENU_PROCESSES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        platformFeature: ProcessPlatformFeatureType.CAN_CREATE_TASK_GROUPS,
      },
    }
  );
  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const filterByObjectsCreatable = BenchlingConfig.PERMISSIONS_ENABLE_PROCESS_SCHEMAS_ACCESS_POLICIES_UI;
  return result.data.processTaskSchemasSupportingPlatformFeature.filter(
    (schema) =>
      !isArchived(schema) &&
      !schema.canOnlyBePartOfFlowchart &&
      userCreatableProcessTaskSchema(schema) &&
      (!filterByObjectsCreatable || schema.permissions.objectsCreatable === true)
  );
}

export function useProcessTaskSchemasForCreate(): Array<ProcessTaskSchemaForCreate> {
  const result = useProcessTaskSchemasForCreateWithLoading();
  if (result == null) {
    return [];
  }
  return result;
}

export function openCreateProcessModal(
  taskSchema: ProcessTaskSchemaForCreate,
  sourceType: string,
  initialFolderId: string | null,
  defaultTaskValuesByColumnType?: DefaultValuesByColumnType
): void {
  const eventGroupId = makeAnalyticsEventId();
  Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
    event_group_id: eventGroupId,
    action_origin: ActionOrigin.SIDEPANEL_GLOBAL_CREATE,
    default_task_creation_view:
      TableOrFormRenderOptionsToAnalyticsType[getInitialCreateTaskModalViewPreference(taskSchema.id)[0]],
    modal_type: CREATE_PROCESS_MODAL_TYPE,
  });
  legacyRunModal(CreateProcessModal, {
    processTaskSchemaId: taskSchema.id,
    processTaskSchemaName: taskSchema.name,
    initialFolderId,
    taskGroupInstructions: taskSchema.taskGroupInstructions,
    sourceType,
    analyticsEventGroupId: eventGroupId,
    defaultTaskValuesByColumnType,
  });
}

interface SchemaWithNameAndOrg {
  id: string;
  name: string;
  organization: {
    name: string;
  };
}

export function getSchemaDisplayNameForCreate(
  currSchema: SchemaWithNameAndOrg,
  taskSchemas: Array<SchemaWithNameAndOrg>
): string {
  const isNameColliding = taskSchemas.some(
    (schema) => schema.name === currSchema.name && schema.id !== currSchema.id
  );

  return `${isNameColliding ? `${currSchema.organization.name} / ` : ''}${currSchema.name}`;
}

export function getProcessTaskSchemaGlobalCreateOptions(
  taskSchemas: Array<ProcessTaskSchemaForCreate>
): Array<CreateMenuItem> {
  return taskSchemas.length === 0
    ? [{disabled: true, name: 'No task schemas', value: 'no-task-schemas'}]
    : taskSchemas.map((taskSchema) => ({
        name: getSchemaDisplayNameForCreate(taskSchema, taskSchemas),
        handleOnClick: () => {
          openCreateProcessModal(taskSchema, 'global_create', null);
        },
        value: taskSchema.id,
      }));
}

export function getProcessTaskSchemaContextualCreateOptions(
  taskSchemas: Array<ProcessTaskSchemaForCreate>,
  folderId: string
): Array<CreateDropdownOption> {
  return taskSchemas.map((taskSchema) => ({
    name: getSchemaDisplayNameForCreate(taskSchema, taskSchemas),
    value: taskSchema.id,
    handleClick: () => {
      openCreateProcessModal(
        taskSchema,
        'global_create', // TODO: Consider fixing this - not global create, but folder create.
        // NOTE: `folderId` does not need to be threaded here due to #7285. By specifying `null`, we allow
        // `CreateProcesModal` to pick a more preferable folder (e.g. one configured on the task schema), and
        // only then fallback to the open folder / user preference.
        null
      );
    },
  }));
}
